import React, {type FC, useMemo} from 'react';
import {ShouldRenderVarsKeys} from '../../../baseCustomCssVars';
import type {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import {useStylesParams} from '../../../stylesParamsContext';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {useAllStyles} from '../../../allStylesContext';

type ConditionalRenderProps = {
  by: Partial<ShouldRenderVarsKeys>;
  dataHook?: string;
} & Omit<React.HTMLProps<HTMLDivElement>, 'style'> &
  IGalleryGlobalProps;

export const ConditionalRender: FC<ConditionalRenderProps> = ({
  by: renderCondition,
  className,
  dataHook,
  children,
  ...props
}: ConditionalRenderProps) => {
  const stylesParams = useStylesParams();
  const {styleParamsForAllBreakpoints, shouldRenderVarsForAllBreakpoints} = useAllStyles();
  const {isEditor} = useEnvironment();

  const shouldRender = useMemo(() => {
    if (renderCondition in stylesParams) {
      return styleParamsForAllBreakpoints.some(
        (stylesParamsPerBreakpoint) => stylesParamsPerBreakpoint.booleans[renderCondition]
      );
    }

    return shouldRenderVarsForAllBreakpoints.some(
      (shouldRenderVarsPerBreakpoint) => shouldRenderVarsPerBreakpoint[renderCondition]
    );
  }, [renderCondition, stylesParams, styleParamsForAllBreakpoints, shouldRenderVarsForAllBreakpoints]);

  if (!isEditor && !shouldRender) {
    return null;
  }

  const cssVariableName = `--${renderCondition.replace(':', '-')}-display`;
  const style: React.CSSProperties = {
    display: `var(${cssVariableName}, inherit)`,
  };

  return (
    <div className={className} data-hook={dataHook} style={style} {...props}>
      {children}
    </div>
  );
};
